import { c as _c } from "react/compiler-runtime";
import { Formik, useField, useFormikContext } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Prompt } from 'react-router-dom';
import styled, { css } from 'styled-components';
import useSWR from 'swr';
import { Grid, GridUnit } from '@hookdeck/theme';
import { ConsoleLine } from '../../../../../../../../../typings/TransformationExecution.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import field_formats from '../../../../../../utils/field-formatters';
import AIPromptDropdown, { getAIErrorMessage } from '../../../../../common/AIPromptDropdown';
import Button from '../../../../../common/base/Button';
import Divider from '../../../../../common/base/Divider';
import Icon from '../../../../../common/base/Icon';
import Keybinds from '../../../../../common/base/Keybinds';
import Text from '../../../../../common/base/Text';
import { useDialog } from '../../../../../common/Dialog';
import Dropdown from '../../../../../common/Dropdown';
import EditorInput from '../../../../../common/Form/Fields/EditorInput';
import TextInput from '../../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { ScreenModal } from '../../../../../common/Modal';
import { useToasts } from '../../../../../common/Toast';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import useSearchQuery from '../../../../../hooks/useSearchQuery';
import TransformationExecutionsView from '../../../Transformation/TransformationExecutionsView';
import Console from './Console';
import IOPanel from './IOPanel';
import Variables from './Variables';
const AI_GENERATION_ERROR = "Transformation couldn't be generated. Try rephrasing your instructions.";
const StyledGridUnit = styled(GridUnit)(({
  theme
}) => css`
    position: relative;
    height: calc(100vh - 52px);
    overflow: hidden;
    border-left: ${theme.border};
  `);
const StyledInputWrapper = styled(Div)<{
  height_offset?: number;
}>(({
  height_offset = 104
}) => css`
    position: relative;
    height: calc(100vh - ${height_offset}px);
    overflow: hidden;
  `);
const StyleConsole = styled(Console)`
  position: absolute;
  bottom: 0;
  left: 0;
`;
let last_checked_name = {
  name: null,
  available: true
};
const TransformRuleFormWrapper: React.FC<{
  onSave: (new_value) => Promise<any>;
  onClose: () => void;
  transformation_id?: string;
  transformation?: {
    code: string;
    env: [string, string][];
    name: string;
  };
  webhook_id?: string;
  source_id?: string;
  promptOnClose?: boolean;
}> = t0 => {
  const $ = _c(24);
  const {
    onSave,
    onClose,
    transformation,
    webhook_id,
    source_id,
    transformation_id,
    promptOnClose: t1
  } = t0;
  const promptOnClose = t1 === undefined ? false : t1;
  const [isNaming, setIsNaming] = useState(false);
  const saved_ref = useRef(false);
  let t2;
  if ($[0] !== transformation?.env) {
    t2 = transformation?.env && transformation?.env.length > 0 ? transformation?.env : [["", ""]];
    $[0] = transformation?.env;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== transformation) {
    t3 = transformation || {};
    $[2] = transformation;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = JSON.stringify({
      headers: {
        "content-type": "application/json"
      },
      body: {
        hello: "world"
      },
      query: "?",
      parsed_query: {}
    }, null, 2);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t3) {
    t5 = {
      vars: t2,
      transformation: t3,
      sample_event_data_id: null,
      input: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onClose || $[9] !== onSave) {
    t6 = (v, t7) => {
      const {
        resetForm
      } = t7;
      return onSave(v).then(() => {
        saved_ref.current = true;
        resetForm({
          values: v
        });
        onClose();
      });
    };
    $[8] = onClose;
    $[9] = onSave;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isNaming || $[12] !== onClose || $[13] !== promptOnClose || $[14] !== source_id || $[15] !== transformation || $[16] !== transformation_id || $[17] !== webhook_id) {
    t7 = t8 => {
      const {
        values,
        handleSubmit,
        isSubmitting
      } = t8;
      return <form onSubmit={handleSubmit}><Prompt when={(!isSubmitting && !isNaming || promptOnClose) && JSON.stringify(values.transformation) !== JSON.stringify(transformation)} message={location => saved_ref.current || location.search.includes("transform_tab") ? true : "Are you sure you want to quit without saving your work?"} /><TransformRuleForm onClose={onClose} handleSubmit={handleSubmit} webhook_id={webhook_id} source_id={source_id} transformation_id={transformation_id} setIsNaming={setIsNaming} /></form>;
    };
    $[11] = isNaming;
    $[12] = onClose;
    $[13] = promptOnClose;
    $[14] = source_id;
    $[15] = transformation;
    $[16] = transformation_id;
    $[17] = webhook_id;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== transformation_id) {
    t8 = <ScreenModal><Formik key={transformation_id} initialValues={t5} validate={_temp} onSubmit={t6}>{t7}</Formik></ScreenModal>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = transformation_id;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};
const TransformRuleForm: React.FC<{
  transformation_id?: string;
  webhook_id?: string;
  source_id?: string;
  onClose: () => void;
  handleSubmit: () => void;
  setIsNaming: (isNaming: boolean) => void;
}> = ({
  webhook_id,
  source_id,
  onClose,
  handleSubmit,
  setIsNaming,
  transformation_id
}) => {
  const [console_lines, setConsoleLines] = useState<ConsoleLine[]>([]);
  const [is_running, setIsRunning] = useState<boolean>(false);
  const {
    query,
    updateSearchQuery
  } = useSearchQuery<{
    input_execution_id?: string;
    executions?: boolean;
  }>();
  const input_execution_id = query.input_execution_id;
  const {
    isValid,
    isSubmitting,
    validateForm
  } = useFormikContext();
  const [{
    value: transformation
  },, {
    setValue: setTransformation,
    setTouched: setTransformationTouched
  }] = useField('transformation');
  const [{
    value: input
  },, {
    setValue: setInput
  }] = useField('input');
  const [{
    value: sample_event_data_id
  },, {
    setValue: setSampleEventDataId
  }] = useField<string | null>('sample_event_data_id');
  const [,, {
    setValue: setOutput
  }] = useField('output');
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const [console_collapsed, setConsoleCollapsed] = useState(false);
  const {
    data: execution
  } = useSWR(query.input_execution_id && transformation_id && APIMethodKeys.transformations.getExecution(transformation_id, input_execution_id), () => HookdeckAPI.transformations.getExecution(transformation_id!, input_execution_id!));
  useEffect(() => {
    if (execution && execution.original_event_data) {
      setInput(JSON.stringify(execution.original_event_data, null, 2));
      setSampleEventDataId(execution.original_event_data_id ?? null);
      setOutput(null);
      setConsoleLines([]);
    }
  }, [execution]);
  const showNameDialog = (submit = false) => {
    setIsNaming(true);
    showDialog((values: {
      name: string;
    }) => {
      // The submit below reads Formik state, so the name must be committed
      // synchronously — otherwise a slow render lets it submit the
      // pre-dialog values and the transformation is saved without a name.
      flushSync(() => {
        setTransformation({
          ...transformation,
          name: values.name
        });
        setTransformationTouched(true);
      });
      setIsNaming(false);
      if (submit) {
        handleSubmit();
      }
    }, () => setIsNaming(false), {
      title: 'Name your transformation',
      submit_icon: 'success',
      submit_label: 'Confirm',
      cancel_label: 'Cancel',
      form_props: {
        initial_values: {
          name: transformation.name || ''
        },
        validate: async ({
          name
        }: {
          name: string;
        }) => {
          if (!name || name.length < 0) return {
            name: 'Required'
          };else if (name !== transformation.name) {
            const used = await HookdeckAPI.transformations.nameIsUsed(name);
            if (used) {
              return {
                name: 'Name is already used.'
              };
            }
          }
          return {};
        },
        Fields: () => <TextInput format={field_formats.slugify} name="name" placeholder="Name" m={0} help="Provide a unique name that represents this transformation." mono required validate={async name_0 => {
          if (!name_0) {
            return 'Required';
          } else {
            const name_changed = name_0 !== last_checked_name.name;
            if (name_changed) {
              const transformations = await HookdeckAPI.transformations.list({
                name: name_0,
                limit: 1
              });
              if (transformations.count > 0 && (!transformation_id || transformations.models[0]?.id !== transformation_id)) {
                last_checked_name = {
                  name: transformation.name,
                  available: false
                };
                return 'Name is already used.';
              }
              last_checked_name = {
                name: transformation.name,
                available: true
              };
            } else if (!last_checked_name.available) {
              return 'Name is already used.';
            }
          }
        }} />
      }
    });
  };
  const getEnv = (): Record<string, string> => transformation.env.reduce((obj, [key, value]) => {
    obj[key] = value;
    return obj;
  }, {});
  const runCode = async (code: string) => {
    if (is_running) {
      return;
    }
    if (!code) {
      return addToast('error', 'Code is empty and cannot be run.');
    }
    const errors = await validateForm();
    if ((errors as any).input) {
      return addToast('error', 'Input is invalid and transformation cannot be run.');
    }
    setIsRunning(true);
    try {
      const response = await HookdeckAPI.transformations.run({
        webhook_id: webhook_id || execution?.webhook_id || undefined,
        request: JSON.parse(input),
        event_data_id: sample_event_data_id ?? undefined,
        code,
        env: getEnv()
      });
      const output = response.request && JSON.stringify(response.request, null, 2);
      setOutput(output);
      setConsoleLines(response.console || []);
      setIsRunning(false);
      if (!output || ['fatal', 'error'].includes(response.log_level)) {
        return addToast('error', 'Code executed but threw an error.');
      }
      if (response.log_level === 'warn') {
        return addToast('warning', 'Code executed but logged a warning.');
      }
      return addToast('success', 'Code successfully executed.');
    } catch (e) {
      setIsRunning(false);
      return addToast('error', `Code failed to execute. Error: ${e.message}`);
    }
  };
  const onRun = () => runCode(transformation?.code);
  const onPromptSubmit = async (prompt: string) => {
    let request;
    try {
      request = JSON.parse(input);
    } catch {
      addToast('error', 'Input is invalid JSON, fix it to generate a transformation.');
      return false;
    }
    try {
      const {
        code: code_0,
        explanation,
        error
      } = await HookdeckAPI.completion.completeTransformation({
        prompt,
        code: transformation.code,
        request,
        env: getEnv(),
        webhook_id: webhook_id || execution?.webhook_id || undefined
      });
      if (!code_0) {
        addToast('error', AI_GENERATION_ERROR);
        return false;
      }
      setTransformation({
        ...transformation,
        code: code_0
      });
      setTransformationTouched(true);
      if (error) {
        addToast('warning', `${explanation || 'Transformation generated.'} It still fails on the input, review it before saving.`);
      } else {
        addToast('success', explanation || 'Transformation generated.');
      }
      runCode(code_0);
      return true;
    } catch (e_0) {
      addToast('error', getAIErrorMessage(e_0, AI_GENERATION_ERROR));
      return false;
    }
  };
  const event = new Event('run');
  useEffect(() => {
    window.addEventListener('run', onRun);
    return () => window.removeEventListener('run', onRun);
  }, [onRun]);
  return transformation_id && query.executions ? <TransformationExecutionsView in_modal onEdit={execution_0 => {
    updateSearchQuery({
      input_execution_id: execution_0?.id,
      executions: undefined
    });
  }} onClose={() => updateSearchQuery({
    executions: undefined
  })} transformation_id={transformation_id} /> : <>
      <Div p={{
      x: 6,
      y: 3
    }} flex={{
      align: 'center',
      justify: 'space-between',
      gap: 4
    }}>
        <Div flex={{
        align: 'center',
        gap: 4
      }} style={{
        minWidth: 0
      }}>
          <Button onClick={onClose} outline icon="close" />
          <Div flex={{
          align: 'center'
        }} style={{
          minWidth: 0
        }}>
            <Icon icon="transformation" left />
            <Text ellipsis semi>
              {transformation!.name || 'New Transformation'}
            </Text>
          </Div>
        </Div>
        <Div flex={{
        align: 'center',
        gap: 3
      }} style={{
        flexShrink: 0
      }}>
          <Button outline icon="edit" onClick={() => showNameDialog(false)}>
            Edit transformation name
          </Button>
          {transformation_id && <Button outline onClick={() => updateSearchQuery({
          executions: true
        })} icon="rule">
              View execution list
            </Button>}
          <Button primary disabled={!isValid || isSubmitting} onClick={() => {
          if (!transformation.name) {
            showNameDialog(true);
          } else {
            handleSubmit();
          }
        }} icon={isSubmitting ? 'loading' : 'success'}>
            Confirm changes
          </Button>
        </Div>
      </Div>
      <Divider />
      <Grid>
        <GridUnit size={1 / 3}>
          <Div style={{
          height: 'calc(100vh - 52px)'
        }} flex={{
          direction: 'column',
          grow: true
        }}>
            <IOPanel source_id={source_id || null} transformation_id={transformation_id || null} webhook_id={webhook_id || null} />
          </Div>
        </GridUnit>
        <StyledGridUnit size={2 / 3}>
          <Div p={{
          l: 5,
          r: 6,
          y: 3
        }} flex={{
          align: 'center',
          justify: 'space-between'
        }}>
            <Button disabled={is_running} ghost onClick={onRun} icon={is_running ? 'loading' : 'play_circle'}>
              Run transformation
              <Keybinds keys={['CMD', '⏎']} />
            </Button>
            <Div flex={{
            align: 'center',
            gap: 2
          }}>
              <AIPromptDropdown label="AI" placeholder="Describe the transformation, e.g. rename customer_id to user_id and remove the metadata field..." submit_label="Generate code" onSubmit={onPromptSubmit} />
              <Dropdown label="Variables" icon="settings" expand_icon="chevron_down" z_index={1080} badge={transformation?.env?.length > 0 ? {
              label: transformation.env.length
            } : undefined} ghost>
                <Variables name="transformation.env" />
              </Dropdown>
            </Div>
          </Div>
          <Divider />
          <StyledInputWrapper height_offset={console_collapsed ? 159 : 335 + 52 + 52}>
            <EditorInput flex={{
            direction: 'column',
            grow: true
          }} language="javascript" display="full" name="transformation.code" type_declarations="transformation" cmdEnter={() => window.dispatchEvent(event)} />
          </StyledInputWrapper>
          <StyleConsole border_top={true} collapseable={true} lines={console_lines} onClear={() => setConsoleLines([])} onToggleCollapse={collapsed => setConsoleCollapsed(collapsed)} />
        </StyledGridUnit>
      </Grid>
    </>;
};
export default TransformRuleFormWrapper;
function _temp(t0) {
  const {
    input
  } = t0;
  if (!input || typeof input !== "string") {
    return {
      input: "Input must be a valid JSON object that represent a input request"
    };
  }
  let parsed_input;
  try {
    parsed_input = JSON.parse(input);
  } catch {
    return {
      input: "Input is not valid JSON"
    };
  }
  const missing_inputs = ["body", "headers"].filter(key => parsed_input[key] === undefined);
  if (missing_inputs.length > 0) {
    return {
      input: `Missing input ${missing_inputs.join(", ")} to run code.`
    };
  }
  return {};
}